import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { ArrowUp, ArrowDown, Minus, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const PerformancePage = () => {
  const [trendFilter, setTrendFilter] = useState({ type: 'all', id: null });
  const [trendRange, setTrendRange] = useState(30);
  const [trends, setTrends] = useState({ data: [], change_pct: 0, total: 0 });
  const [deadListings, setDeadListings] = useState([]);
  const [pharmacyRanking, setPharmacyRanking] = useState([]);
  const [productRanking, setProductRanking] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchTrends();
  }, [trendFilter, trendRange]);

  const fetchAll = async () => {
    try {
      const [pharmsRes, prodsRes, deadRes, pharmRankRes, prodRankRes, matrixRes] = await Promise.all([
        api.get('/pharmacies'),
        api.get('/products'),
        api.get('/performance/dead-listings'),
        api.get('/performance/pharmacy-ranking'),
        api.get('/performance/product-ranking'),
        api.get('/performance/product-pharmacy-matrix').catch(() => ({ data: null }))
      ]);
      setPharmacies(pharmsRes.data);
      setProducts(prodsRes.data);
      setDeadListings(deadRes.data);
      setPharmacyRanking(pharmRankRes.data);
      setProductRanking(prodRankRes.data);
      setMatrix(matrixRes.data);
    } catch (error) { console.error(error); }
  };

  const fetchTrends = async () => {
    try {
      const params = new URLSearchParams({ filter_type: trendFilter.type, range_days: trendRange });
      if (trendFilter.id) params.append('filter_id', trendFilter.id);
      const res = await api.get(`/performance/trends?${params}`);
      setTrends(res.data);
    } catch (error) { console.error(error); }
  };

  const handleAction = async (action, dl) => {
    try {
      await api.post('/performance/dead-listings/action', { action, package_id: dl.package_id, pharmacy_id: dl.pharmacy_id });
      toast.success(action === 'unlist' ? 'Package unlisted' : action === 'recall' ? 'Recall DO created' : 'Partner notified');
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleUnlist = (pkgId, pharmId) => handleAction('unlist', { package_id: pkgId, pharmacy_id: pharmId });

  const trendIcon = (trend) => {
    if (trend === 'up') return <ArrowUp size={16} className="text-green-600 inline" weight="bold" />;
    if (trend === 'down') return <ArrowDown size={16} className="text-red-600 inline" weight="bold" />;
    return <Minus size={16} className="text-slate-400 inline" weight="bold" />;
  };

  const tagColor = (tag) => {
    if (tag === 'Growing') return 'bg-green-50 text-green-700 border-green-200';
    if (tag === 'Declining' || tag === 'Consider delisting' || tag === 'Review partnership') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>Performance & Rankings</h1>
      </div>

      <Card className="p-6">
        <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Trend Explorer</h2>
          <div className="flex gap-2 flex-wrap">
            <Select value={trendFilter.type} onValueChange={(v) => setTrendFilter({ type: v, id: null })}>
              <SelectTrigger data-testid="trend-what" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sales</SelectItem>
                <SelectItem value="distributor">Distributor</SelectItem>
                <SelectItem value="partner">Specific Partner</SelectItem>
                <SelectItem value="pharmacy">Specific Pharmacy</SelectItem>
                <SelectItem value="product">Specific Product</SelectItem>
              </SelectContent>
            </Select>
            {trendFilter.type === 'pharmacy' && (
              <Select value={trendFilter.id || ''} onValueChange={(v) => setTrendFilter({ ...trendFilter, id: v })}>
                <SelectTrigger className="w-48"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{pharmacies.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {trendFilter.type === 'product' && (
              <Select value={trendFilter.id || ''} onValueChange={(v) => setTrendFilter({ ...trendFilter, id: v })}>
                <SelectTrigger className="w-48"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Select value={String(trendRange)} onValueChange={(v) => setTrendRange(parseInt(v))}>
              <SelectTrigger data-testid="trend-range" className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="365">12 months</SelectItem>
                <SelectItem value="730">24 months</SelectItem>
                <SelectItem value="1095">36 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mb-4">
          <span className="text-sm text-slate-500">Overall change: </span>
          <span className={`font-bold ${trends.change_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{trends.change_pct >= 0 ? '+' : ''}{trends.change_pct}%</span>
          <span className="ml-4 text-sm text-slate-500">Total: <strong className="text-slate-900">RM {trends.total?.toFixed(2)}</strong></span>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={trends.data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="period" stroke="#64748B" fontSize={11} />
            <YAxis stroke="#64748B" />
            <Tooltip />
            <Line type="monotone" dataKey="amount" stroke="#1E3A8A" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {deadListings.length > 0 && (
        <Card className="p-6 border-l-4 border-red-500">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Warning size={24} className="text-red-600" weight="fill" /> Dead-Listing Alerts ({deadListings.length})
          </h2>
          <div className="space-y-2">
            {deadListings.map((dl, idx) => (
              <div key={idx} data-testid={`dead-listing-${idx}`} className="p-3 bg-red-50 rounded-lg">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <p className="font-semibold text-red-900">{dl.package_name} @ {dl.pharmacy_name}</p>
                    <p className="text-sm text-red-700">{dl.months_zero_sales} months of zero sales (threshold: {dl.threshold})</p>
                    {dl.partner_owned && <p className="text-xs text-slate-500">Partner-owned: {dl.partner_name || '—'}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`dl-unlist-${idx}`} onClick={() => handleAction('unlist', dl)}>Unlist</Button>
                    <Button size="sm" variant="outline" data-testid={`dl-recall-${idx}`} onClick={() => handleAction('recall', dl)}>Recall DO</Button>
                    {dl.partner_owned && <Button size="sm" variant="outline" data-testid={`dl-notify-${idx}`} onClick={() => handleAction('notify_partner', dl)}>Notify Partner</Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Tabs defaultValue="pharmacy-rank">
        <TabsList>
          <TabsTrigger value="pharmacy-rank">Pharmacy Ranking</TabsTrigger>
          <TabsTrigger value="product-rank">Product Ranking</TabsTrigger>
          <TabsTrigger value="matrix">Product × Pharmacy Matrix</TabsTrigger>
        </TabsList>
        <TabsContent value="pharmacy-rank">
          <Card className="p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="pb-3 font-semibold">#</th>
                  <th className="pb-3 font-semibold">Pharmacy</th>
                  <th className="pb-3 font-semibold text-right">Last Month (RM)</th>
                  <th className="pb-3 font-semibold text-right">Growth %</th>
                  <th className="pb-3 font-semibold">Trend</th>
                  <th className="pb-3 font-semibold">Tag</th>
                </tr>
              </thead>
              <tbody>
                {pharmacyRanking.map((p, idx) => (
                  <tr key={p.pharmacy_id} className="border-b border-slate-100">
                    <td className="py-3 font-bold">{idx + 1}</td>
                    <td className="py-3">{p.pharmacy_name}</td>
                    <td className="py-3 text-right font-bold">{p.last_month_sales.toFixed(2)}</td>
                    <td className={`py-3 text-right ${p.growth_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{p.growth_pct >= 0 ? '+' : ''}{p.growth_pct}%</td>
                    <td className="py-3">{trendIcon(p.trend)}</td>
                    <td className="py-3"><Badge className={tagColor(p.tag)}>{p.tag}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
        <TabsContent value="product-rank">
          <Card className="p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="pb-3 font-semibold">#</th>
                  <th className="pb-3 font-semibold">Product</th>
                  <th className="pb-3 font-semibold">Owner</th>
                  <th className="pb-3 font-semibold text-right">Units</th>
                  <th className="pb-3 font-semibold text-right">Last Month (RM)</th>
                  <th className="pb-3 font-semibold text-right">Growth %</th>
                  <th className="pb-3 font-semibold">Trend</th>
                  <th className="pb-3 font-semibold">Tag</th>
                </tr>
              </thead>
              <tbody>
                {productRanking.map((p, idx) => (
                  <tr key={p.product_id} className="border-b border-slate-100">
                    <td className="py-3 font-bold">{idx + 1}</td>
                    <td className="py-3">{p.product_name}</td>
                    <td className="py-3 text-xs">{p.owner_name || (p.owner_type === 'partner' ? 'Partner' : 'OctoBoots Distribution')}</td>
                    <td className="py-3 text-right">{p.last_month_units} {p.unit_label}</td>
                    <td className="py-3 text-right font-bold">{p.last_month_sales.toFixed(2)}</td>
                    <td className={`py-3 text-right ${p.growth_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{p.growth_pct >= 0 ? '+' : ''}{p.growth_pct}%</td>
                    <td className="py-3">{trendIcon(p.trend)}</td>
                    <td className="py-3"><Badge className={tagColor(p.tag)}>{p.tag}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
        <TabsContent value="matrix">
          <Card className="p-6 overflow-x-auto">
            {matrix && (
              <div>
                <p className="text-sm text-slate-500 mb-2">Last month unit sales — period {matrix.period}</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 text-left bg-slate-50">
                      <th className="p-2 font-semibold">Product</th>
                      {matrix.pharmacies.map(ph => <th key={ph.id} className="p-2 font-semibold text-right">{ph.name}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.products.map(p => (
                      <tr key={p.id} className="border-b border-slate-100">
                        <td className="p-2 font-medium">{p.product_name}</td>
                        {matrix.pharmacies.map(ph => (
                          <td key={ph.id} className="p-2 text-right">
                            <span className={p.by_pharmacy[ph.id] === 0 ? 'text-slate-400' : 'text-slate-800 font-semibold'}>
                              {p.by_pharmacy[ph.id]} <span className="text-xs text-slate-400">{p.unit_label}</span>
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
